import { useState } from "react";
import { JOB_STEPS } from "../CreateJobPage";
import CustomInputV2 from "@components/CustomInputV2/CustomInputV2";

const PAY_TYPES = [
  {
    label: "Annual salary",
    value: 1,
  },
  {
    label: "Daily rate",
    value: 2,
  },
  {
    label: "Hourly rate",
    value: 3,
  },
];

const Step2 = ({ activeStep }) => {
  const [selectedPostType, setSelectedPostType] = useState(1);

  return (
    <>
      {/* Header */}
      <div className="flex border-b pb-3">
        <div className="flex flex-col gap-0.5 flex-1">
          <span className="text-xss font-bold text-light-black uppercase">
            Step {String(activeStep).padStart(2, "0")} /{" "}
            {JOB_STEPS[activeStep - 1]?.label}{" "}
          </span>
          <span className="font-semibold text-xl">
            {JOB_STEPS[activeStep - 1]?.title}
          </span>
          <span className="text-xss font-medium text-light-black">
            {JOB_STEPS[activeStep - 1]?.subtitle}
          </span>
        </div>
        <span className="self-end text-xss font-bold text-light-black uppercase leading-3">
          {JOB_STEPS[activeStep - 1]?.tagline}
        </span>
      </div>
      {/* Matching context */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              01
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">Matching context</span>
              <span className="text-xss font-medium text-light-black">
                Establish the professional context before entering the public
                job title.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Required for matching
          </div>
        </div>

        <div className="grid grid-cols-[1fr_1fr] gap-4 p-4">
          <div className="flex flex-col gap-3 pr-24">
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Classification
              </label>
              <CustomInputV2 placeholder="Company name" />
              <span className="text-xsss text-light-black font-medium">
                Choose the broad professional field this role belongs to.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Job function or discipline
              </label>
              <CustomInputV2 placeholder="Requirement Code" />
              <span className="text-xsss text-light-black font-medium">
                Narrows the role context used for title and skill matching.
              </span>
            </div>
          </div>
          <div className="flex flex-col border-l-[3px] border-slate-600 bg-slate-50 px-5 justify-center gap-2">
            <span className="uppercase font-bold text-xss text-slate-600 tracking-wider">
              Why this comes first
            </span>
            <span className="text-xss font-medium text-light-black">
              A title can mean different things across industries. COMTRACKED
              keeps this classification and discipline attached to the job so
              later skill comparisons are made in the correct context.
            </span>
            <span className="font-semibold text-xss">
              Information Technology / Cyber security
            </span>
          </div>
        </div>
      </div>

      {/* Role and working arrangement */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              02
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">
                Role and working arrangement
              </span>
              <span className="text-xss font-medium text-light-black">
                Imported facts remain editable before the post is published.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Candidate-facing facts
          </div>
        </div>

        <div className="flex flex-col p-4 gap-3">
          <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Job title
              </label>
              <CustomInputV2 placeholder="Company name" />
              <span className="text-xsss text-light-black font-medium">
                Use the recognisable public title; the matching context is
                stored separately.
              </span>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Experience level
              </label>
              <CustomInputV2 placeholder="Company name" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Location
              </label>
              <CustomInputV2 placeholder="Company name" />
            </div>
          </div>

          <div className="grid grid-cols-[1fr_1fr_1fr] gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Workplace type
              </label>
              <CustomInputV2 placeholder="Requirement Code" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Employment type
              </label>
              <CustomInputV2 placeholder="Requirement Code" />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Job post duration
              </label>
              <CustomInputV2 placeholder="Requirement Code" />
            </div>
          </div>

          <div className="flex flex-col gap-1.5 w-72">
            <label className="text-xss font-bold text-slate-600">
              Qualification or eligibility
            </label>
            <CustomInputV2 placeholder="Requirement Code" />
          </div>
        </div>
      </div>

      {/* Compensation */}
      <div className="rounded-md border overflow-hidden mt-5">
        <div className="flex items-center justify-between border-b px-4 py-2.5 bg-slate-50">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center rounded-full h-7 w-7 bg-slate-50 border text-xss font-bold text-light-black">
              03
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-xs">Compensation </span>
              <span className="text-xss font-medium text-light-black">
                Describe pay in the unit candidates will actually compare.
              </span>
            </div>
          </div>

          <div className="rounded-full px-2 py-1 font-bold text-xsss text-green-800 uppercase bg-green-100">
            Shown on the job post
          </div>
        </div>

        <div className="flex flex-col gap-4 p-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-xss font-bold text-slate-600">
              How is this role paid?
            </label>
            <div className="flex items-center gap-2">
              {PAY_TYPES?.map((payType) => {
                return (
                  <button className="flex items-center justify-center border rounded-md h-10 flex-1 text-xss font-bold text-slate-600 hover:bg-slate-50">
                    {payType?.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-end gap-3">
            <div className="flex  flex-col gap-1.5">
              <label className="text-xss font-bold text-slate-600">
                Currency
              </label>
              <CustomInputV2 />
            </div>
            <div className="flex flex-col rounded-md border bg-slate-50 flex-1 p-2 gap-1">
              <span className="text-xss font-bold text-slate-600">
                Pay range
              </span>
              <div className="flex items-end justify-between gap-3">
                <div className="flex  flex-col gap-1.5 flex-1">
                  <label className="text-xss font-semibold text-gray-500">
                    FROM
                  </label>
                  <CustomInputV2 />
                </div>
                <span className="text-xss text-center mb-3">to</span>
                <div className="flex  flex-col gap-1.5 flex-1">
                  <label className="text-xss font-semibold text-gray-500">
                    TO
                  </label>
                  <CustomInputV2 />
                </div>
                <div className="font-bold text-xss text-light-black border-l pl-2 flex items-center h-10">
                  per year
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border rounded-md px-3.5 py-3 gap-1.5 ">
            <label className="uppercase text-xsss font-bold text-light-black tracking-wider">
              candidate-facing pay
            </label>
            <span className="text-content-muted text-xss font-bold">
              £65–£78,000 per year
            </span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Step2;
